import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION } from '../config/foundation';

const POLICY_GROUPS: { title: string; policies: string[] }[] = [
  {
    title: 'Safeguarding & people',
    policies: [
      'Safeguarding policy and procedures',
      'Safeguarding vulnerable beneficiaries',
      'Bullying and harassment policy and procedures',
      'Volunteer management',
      'Serious incident reporting policy and procedures',
    ],
  },
  {
    title: 'Governance & trustees',
    policies: [
      'Trustee conflicts of interest policy and procedures',
      'Conflicting interests',
      'Trustee expenses policy and procedures',
      'Complaints policy and procedures',
      'Complaints handling',
    ],
  },
  {
    title: 'Finance & risk',
    policies: [
      'Internal charity financial controls policy and procedures',
      'Financial reserves policy and procedures',
      'Internal risk management policy and procedures',
      'Investing charity funds policy and procedures',
    ],
  },
  {
    title: 'Communications & activity',
    policies: [
      'Social media policy and procedures',
      'Campaigns and political activity policy and procedures',
      'Engaging external speakers at charity events policy and procedures',
    ],
  },
];

export function Policies() {
  usePageTitle('Our Policies');
  return (
    <Layout>
      <PageHero eyebrow="Governance" title={<>Our policies</>}>
        We are committed to operating safely, transparently, and in the best interests of the
        people we serve. These are the policies that govern our work.
      </PageHero>

      {/* Safeguarding highlight */}
      <section className="py-12 sm:py-16">
        <div className="section">
          <Reveal className="max-w-3xl mx-auto">
            <div className="card p-6 sm:p-8 flex flex-col sm:flex-row gap-5 items-start">
              <div className="w-11 h-11 rounded-xl bg-brand-gradient text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-mustard-green-dark/15">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
              <div>
                <h3 className="font-display font-bold text-mustard-green-dark mb-1">Safeguarding is our priority</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  The safety and wellbeing of our beneficiaries, volunteers, and staff comes first.
                  We have robust safeguarding policies in place and take all concerns seriously. If you
                  have a safeguarding concern, please contact us immediately at{' '}
                  <a href={`mailto:${FOUNDATION.email}`} className="text-mustard-green font-semibold">{FOUNDATION.email}</a>.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Policy groups */}
      <section className="pb-8">
        <div className="section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {POLICY_GROUPS.map((group, i) => (
              <Reveal key={group.title} delay={(i % 2) * 120}>
                <div className="card h-full p-6 sm:p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gold-gradient" aria-hidden="true" />
                  <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-4">{group.title}</h2>
                  <ul className="space-y-2.5">
                    {group.policies.map((policy) => (
                      <li key={policy} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-mustard-gold flex-shrink-0 mt-2" />
                        {policy}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Request */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="max-w-2xl mx-auto text-center">
            <p className="eyebrow mb-3">Transparency</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-display mb-4">
              <span className="text-gradient">Request a copy</span>
            </h2>
            <p className="text-slate-500 leading-relaxed mb-6">
              Copies of any of our policies are available on request. {FOUNDATION.name} is a{' '}
              {FOUNDATION.structure} registered in England and Wales (Charity No.{' '}
              {FOUNDATION.charityNumber}); our full governing document and registration details are
              held on the Charity Commission register.
            </p>
            <a href={`mailto:${FOUNDATION.email}?subject=${encodeURIComponent('Policy request')}`} className="btn-primary inline-block">
              Request a policy
            </a>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
